import { useQuery } from 'react-query';
import { toast } from 'react-toastify';
import useAuthStore from '../store/authStore';
import { getAllSeries, getFeaturedSeries, getPopularSeries, getSeriesByCategory, getSeriesByLanguage, getSeriesByLanguageAndType, getSeriesBySlug, getSeriesByType, getSeriesByTypeAndCategory, getSimilarSeries, getUniqueSeriesByType } from '../api/seriesApi';


export const useSeries = (params = {}) => {
  const { setError } = useAuthStore();

  const {
    data,
    isLoading,
    error,
    refetch
  } = useQuery(['series', params], () => getAllSeries(params), {
    onError: (error) => {
      const errorMessage = error?.response?.data?.message || 'Failed to fetch series';
      setError(errorMessage);
      toast.error(errorMessage);
    },
    retry: false,
    staleTime: 300000, // 5 minutes
    cacheTime: 3600000, // 1 hour
  });

  return {
    series: data?.series || [],
    pagination: data?.pagination,
    isLoading,
    error,
    refetchSeries: refetch
  };
};

export const useSeriesBySlug = (slug) => {
  const { setError } = useAuthStore();

  const {
    data: series,
    isLoading,
    error,
    refetch
  } = useQuery(
    ['series', slug],
    () => getSeriesBySlug(slug),
    {
      onError: (error) => {
        const errorMessage = error?.response?.data?.message || 'Failed to fetch series details';
        setError(errorMessage);
        toast.error(errorMessage);
      },
      retry: 1,
      staleTime: 300000,
      cacheTime: 3600000,
      enabled: slug !== undefined && slug !== null,
    }
  );

  return {
    series,
    isLoading,
    error,
    refetchSeries: refetch
  };
};

export const useSeriesByType = (type, page = 1, limit = 10) => {
  const { setError } = useAuthStore();

  const {
    data,
    isLoading,
    error,
    refetch
  } = useQuery(
    ['seriesByType', type, page, limit],
    () => getSeriesByType(type, page, limit),
    {
      onError: (error) => {
        const errorMessage = error?.response?.data?.message || 'Failed to fetch series by type';
        setError(errorMessage);
        toast.error(errorMessage);
      },
      keepPreviousData: true,
      staleTime: 300000, // 5 minutes
      cacheTime: 3600000, // 1 hour
      enabled: type !== undefined && type !== null,
    }
  );

  return {
    series: data?.series || [],
    pagination: data?.pagination,
    isLoading,
    error,
    refetchSeries: refetch
  };
};

export const useSeriesByCategory = (categoryId, page = 1, limit = 10) => {
  const { setError } = useAuthStore();

  const {
    data,
    isLoading,
    error,
    refetch
  } = useQuery(
    ['seriesByCategory', categoryId, page, limit],
    () => getSeriesByCategory(categoryId, page, limit),
    {
      onError: (error) => {
        const errorMessage = error?.response?.data?.message || 'Failed to fetch series by category';
        setError(errorMessage);
        toast.error(errorMessage);
      },
      keepPreviousData: true,
      staleTime: 300000, // 5 minutes
      cacheTime: 3600000, // 1 hour
      enabled: categoryId !== undefined && categoryId !== null,
    }
  );

  return {
    series: data?.series || [],
    pagination: data?.pagination,
    isLoading,
    error,
    refetchSeries: refetch
  };
};

export const useFeaturedSeries = (limit = 5) => {
  const { setError } = useAuthStore();

  const {
    data,
    isLoading,
    error,
    refetch
  } = useQuery(
    ['featuredSeries', limit],
    () => getFeaturedSeries(limit),
    {
      onError: (error) => {
        const errorMessage = error?.response?.data?.message || 'Failed to fetch featured series';
        setError(errorMessage);
        toast.error(errorMessage);
      },
      staleTime: 300000, // 5 minutes
      cacheTime: 3600000, // 1 hour
    }
  );

  return {
    series: data?.series || [],
    isLoading,
    error,
    refetchFeaturedSeries: refetch
  };
};

export const usePopularSeries = (limit = 5) => {
  const { setError } = useAuthStore();

  const {
    data,
    isLoading,
    error,
    refetch
  } = useQuery(
    ['popularSeries', limit],
    () => getPopularSeries(limit),
    {
      onError: (error) => {
        const errorMessage = error?.response?.data?.message || 'Failed to fetch popular series';
        setError(errorMessage);
        toast.error(errorMessage);
      },
      staleTime: 300000, // 5 minutes
      cacheTime: 3600000, // 1 hour
    }
  );

  return {
    series: data?.series || [],
    isLoading,
    error,
    refetchPopularSeries: refetch
  };
};

export const useSeriesByTypeAndCategory = (type, categoryId, page = 1, limit = 10) => {
  const { setError } = useAuthStore();

  const {
    data,
    isLoading,
    error,
    refetch
  } = useQuery(
    ['seriesByTypeAndCategory', type, categoryId, page, limit],
    () => getSeriesByTypeAndCategory(type, categoryId, page, limit),
    {
      onError: (error) => {
        const errorMessage = error?.response?.data?.message || 'Failed to fetch series by type and category';
        setError(errorMessage);
        toast.error(errorMessage);
      },
      keepPreviousData: true,
      staleTime: 300000, // 5 minutes
      cacheTime: 3600000, // 1 hour
      enabled: type !== undefined && type !== null && categoryId !== undefined && categoryId !== null,
    }
  );

  return {
    series: data?.series || [],
    pagination: data?.pagination,
    isLoading,
    error,
    refetchSeries: refetch
  };
};

export const useSeriesByLanguage = (language, page = 1, limit = 10) => {
  const { setError } = useAuthStore();

  const {
    data,
    isLoading,
    error,
    refetch
  } = useQuery(
    ['seriesByLanguage', language, page, limit],
    () => getSeriesByLanguage(language, page, limit),
    {
      onError: (error) => {
        const errorMessage = error?.response?.data?.message || 'Failed to fetch series by language';
        setError(errorMessage);
        toast.error(errorMessage);
      },
      keepPreviousData: true,
      staleTime: 300000, // 5 minutes
      cacheTime: 3600000, // 1 hour
      enabled: language !== undefined && language !== null,
    }
  );

  return {
    series: data?.series || [],
    pagination: data?.pagination,
    isLoading,
    error,
    refetchSeries: refetch
  };
};

export const useSeriesByLanguageAndType = (language, type, page = 1, limit = 10) => {
  const { setError } = useAuthStore();

  const {
    data,
    isLoading,
    error,
    refetch
  } = useQuery(
    ['seriesByLanguageAndType', language, type, page, limit],
    () => getSeriesByLanguageAndType(language, type, page, limit),
    {
      onError: (error) => {
        const errorMessage = error?.response?.data?.message || 'Failed to fetch series by language and type';
        setError(errorMessage);
        toast.error(errorMessage);
      },
      keepPreviousData: true,
      staleTime: 300000, // 5 minutes
      cacheTime: 3600000, // 1 hour
      enabled: language !== undefined && language !== null && type !== undefined && type !== null,
    }
  );

  return {
    series: data?.series || [],
    pagination: data?.pagination,
    isLoading,
    error,
    refetchSeries: refetch
  };
};

export const useSimilarSeries = (seriesId) => {
  const { setError } = useAuthStore();

  const {
    data,
    isLoading,
    error,
    refetch
  } = useQuery(
    ['similarSeries', seriesId],
    () => getSimilarSeries(seriesId),
    {
      onError: (error) => {
        const errorMessage = error?.response?.data?.message || 'Failed to fetch similar series';
        setError(errorMessage);
        toast.error(errorMessage);
      },
      staleTime: 300000, // 5 minutes
      cacheTime: 3600000, // 1 hour
      enabled: seriesId !== undefined && seriesId !== null,
    }
  );

  return {
    series: data || [],
    isLoading,
    error,
    refetchSimilarSeries: refetch
  };
};

export const useUniqueSeriesByType = (type, page = 1, limit = 10) => {
  const { setError } = useAuthStore();

  const {
    data,
    isLoading,
    error,
    refetch
  } = useQuery(
    ['seriesUniqueByType', type, page, limit],
    () => getUniqueSeriesByType(type, page, limit),
    {
      onError: (error) => {
        const errorMessage = error?.response?.data?.message || 'Failed to fetch series by type';
        setError(errorMessage);
        toast.error(errorMessage);
      },
      keepPreviousData: true,
      staleTime: 300000, // 5 minutes
      cacheTime: 3600000, // 1 hour
      enabled: type !== undefined && type !== null,
    }
  );

  return {
    series: data?.series || [],
    pagination: data?.pagination,
    isLoading,
    error,
    refetchSeries: refetch
  };
};
